import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { CONTACT_INFO } from "@/lib/pulseConstants";

const LAST_UPDATED = "14 July 2026";

function Section({ title, children, testId }) {
    return (
        <section className="mb-10" data-testid={testId}>
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4 leading-tight">
                {title}
            </h2>
            <div className="space-y-4 body-md">{children}</div>
        </section>
    );
}

export default function TermsPage() {
    useEffect(() => {
        document.title = "Terms of Use \u2014 REFORM Pulse";
    }, []);

    return (
        <div data-testid="terms-page">
            <section className="relative pt-24 md:pt-28 lg:pt-32 pb-14 border-b border-white/10">
                <div className="container-editorial">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-white transition-colors min-h-11 py-3"
                        data-testid="terms-back-link"
                    >
                        <ArrowLeft size={14} /> Back to Home
                    </Link>
                    <SectionEyebrow index="//" label="Legal" />
                    <Reveal>
                        <h1 className="display-1 text-white text-balance max-w-4xl">
                            Terms of <span className="text-[#FF2A2A]">Use.</span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-lg mt-6 max-w-2xl">
                            The terms that govern your use of the REFORM Pulse website.
                        </p>
                    </Reveal>
                    <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.24em] text-white/50">
                        Last updated: {LAST_UPDATED}
                    </div>
                </div>
            </section>

            <section className="section-pad">
                <div className="container-editorial">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        <div className="lg:col-span-8">
                            <Section title="About these terms" testId="terms-about">
                                <p>
                                    These Terms of Use (“<strong>Terms</strong>”) apply to your use of <a href="https://reform-pulse.com" className="text-white underline hover:text-[#FF2A2A]">reform-pulse.com</a> (the “<strong>Website</strong>”), operated by REFORM Pulse, the live-experience arm of REFORM Group. By accessing or using the Website you agree to these Terms. If you do not agree with them, please do not use the Website.
                                </p>
                            </Section>

                            <Section title="Purpose of the Website" testId="terms-purpose">
                                <p>
                                    The Website is provided for general business, informational and enquiry purposes. It gives an overview of REFORM Pulse’s point of view, capabilities, selected work and creative territories, and allows visitors to submit a brief or enquiry.
                                </p>
                                <p>
                                    Information on the Website is provided on an “as is” basis. While we take reasonable care to keep it accurate and up to date, we make no warranty that all content is complete, current or free from errors at every moment.
                                </p>
                            </Section>

                            <Section title="Enquiries and brief submissions" testId="terms-enquiries">
                                <p>
                                    Submitting an enquiry or brief through the Website is an invitation to start a conversation. It does not, by itself, create a client-agency relationship, and it does not commit either party to any project, scope, pricing or timeline.
                                </p>
                                <p>
                                    Any proposal, pricing, deliverable, scope or commercial terms discussed with you are indicative until they are formally documented and accepted in writing.
                                </p>
                                <p>
                                    Commercial terms become binding only when accepted in writing by authorised representatives of the relevant parties. Acceptance may be recorded through a signed proposal, quotation, engagement letter, statement of work, letter of award, purchase order or other agreed written confirmation.
                                </p>
                                <p>
                                    We reserve the right to decline any enquiry, at our discretion, for any lawful reason.
                                </p>
                            </Section>

                            <Section title="Intellectual property" testId="terms-ip">
                                <p>
                                    Unless otherwise indicated, all text, imagery, graphics, video, layout, code and other content on the Website is the property of REFORM Pulse, REFORM Group or the respective rights holders, and is protected by applicable intellectual property laws.
                                </p>
                                <p>
                                    You may view and share links to the Website for lawful personal or business reference. You may not copy, reproduce, republish, adapt or exploit substantial portions of the Website’s content for commercial purposes without our prior written consent.
                                </p>
                                <p>
                                    Third-party names, brand names, product names, logos and trademarks displayed on the Website remain the property of their respective owners and are used for identification and reference only. Their appearance does not imply endorsement, partnership or affiliation beyond any relationship expressly described.
                                </p>
                            </Section>

                            <Section title="Creative territories and case studies" testId="terms-creative">
                                <p>
                                    The “Creative Territories” section of the Website presents speculative concept directions and creative possibilities. Unless stated otherwise, these are illustrative starting points, not delivered client work, and should not be interpreted as case studies or completed engagements.
                                </p>
                                <p>
                                    Case studies presented in the “Selected Work” section describe engagements delivered by REFORM Pulse or the wider REFORM team. Figures, dates and third-party names in those case studies are shared in good faith based on the information available to us at the time of publication.
                                </p>
                            </Section>

                            <Section title="External links" testId="terms-external">
                                <p>
                                    The Website may link to external websites, including REFORM Group and third-party platforms. These links are provided for convenience only. We do not operate or control the content of external websites, and we are not responsible for their content, availability or practices. Visiting an external website is at your own discretion.
                                </p>
                            </Section>

                            <Section title="Limitation of liability" testId="terms-liability">
                                <p>
                                    To the fullest extent permitted by applicable law, REFORM Pulse, REFORM Group and their respective officers, employees, partners and contractors will not be liable for any indirect, incidental, consequential, special or punitive damages arising out of or in connection with your use of, or inability to use, the Website, including any loss of profits, revenue, business opportunity, data or goodwill.
                                </p>
                                <p>
                                    Nothing in these Terms excludes or limits any liability that cannot be lawfully excluded or limited.
                                </p>
                            </Section>

                            <Section title="Privacy" testId="terms-privacy">
                                <p>
                                    Your submission of information through the Website is also governed by our <Link to="/privacy" className="text-white underline hover:text-[#FF2A2A]">Privacy Policy</Link>, which forms part of these Terms.
                                </p>
                            </Section>

                            <Section title="Changes to these Terms" testId="terms-changes">
                                <p>
                                    We may update these Terms from time to time. Changes take effect when posted on this page, and the “Last updated” date at the top will be revised accordingly. Your continued use of the Website after a change is posted constitutes acceptance of the updated Terms.
                                </p>
                            </Section>

                            <Section title="Governing law" testId="terms-law">
                                <p>
                                    These Terms are governed by and construed in accordance with the laws of Malaysia. Any dispute arising out of or in connection with these Terms or your use of the Website will be subject to the exclusive jurisdiction of the courts of Malaysia.
                                </p>
                            </Section>

                            <Section title="Contact" testId="terms-contact">
                                <p>
                                    For any question about these Terms, contact:
                                </p>
                                <p>
                                    <a
                                        href={`mailto:${CONTACT_INFO.email}`}
                                        className="inline-flex items-center gap-2 text-white hover:text-[#FF2A2A] transition-colors min-h-11 py-2"
                                        data-testid="terms-email-link"
                                    >
                                        <Mail size={16} /> {CONTACT_INFO.email}
                                    </a>
                                </p>
                            </Section>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
